import { z } from 'zod';
import { formatPhone, normalizePhone } from '../phone.js';
import { countSegments, toGsmSafe } from '../sms/segments.js';

/**
 * `workspaces.settings.compliance` (PLAN.md §3.4, §7; owner decisions 2026-10-12): the opt-out footer
 * and the three automatic replies (opt-out confirmation, resubscribe confirmation, HELP reply).
 *
 * Stored in snake_case because SQL reads it too (`app.first_message_footer`, the settings backstop
 * trigger). Texts are templates: `{workspace}` becomes the workspace name and `{help_contact}` the help
 * email or phone. Every automatic reply must render to ONE GSM-7 segment. The settings screen refuses
 * anything else; the worker falls back to the defaults if a later rename makes a stored text too long,
 * and the opt-out confirmation always has a text that fits (it must go out, whatever the settings say).
 */

export const DEFAULT_OPT_OUT_FOOTER = 'Reply STOP to opt out';
export const OPT_OUT_FOOTER_MAX = 60;
export const TEMPLATE_MAX = 200;
export const HELP_CONTACT_MAX = 80;

export const DEFAULT_OPT_OUT_CONFIRMATION =
  '{workspace}: you are unsubscribed and will get no more texts. Reply START to resubscribe.';
export const DEFAULT_RESUBSCRIBE_CONFIRMATION =
  '{workspace}: you are resubscribed. Reply STOP to opt out.';
const DEFAULT_HELP_EMAIL =
  '{workspace}: reply here to reach your contact, or email {help_contact}. Msg&data rates may apply. Reply STOP to opt out.';
const DEFAULT_HELP_PHONE =
  '{workspace}: reply here to reach your contact, or call {help_contact}. Msg&data rates may apply. Reply STOP to opt out.';

/** Last resort for the opt-out confirmation: fits one GSM-7 segment whatever the workspace is called. */
export const FALLBACK_OPT_OUT_CONFIRMATION =
  'You are unsubscribed and will get no more texts. Reply START to resubscribe.';
const FALLBACK_RESUBSCRIBE_CONFIRMATION = 'You are resubscribed. Reply STOP to opt out.';

export type HelpContact = { kind: 'email'; value: string } | { kind: 'phone'; value: string };

const EmailSchema = z.email();

/** An email address or a phone number (normalized to E.164). */
export function parseHelpContact(
  input: string,
): { ok: true; contact: HelpContact } | { ok: false; reason: string } {
  const value = input.trim();
  if (value === '') return { ok: false, reason: 'Enter an email address or a phone number' };
  if (value.length > HELP_CONTACT_MAX) return { ok: false, reason: 'Use at most 80 characters' };
  if (value.includes('@')) {
    return EmailSchema.safeParse(value).success
      ? { ok: true, contact: { kind: 'email', value: value.toLowerCase() } }
      : { ok: false, reason: 'Enter a valid email address' };
  }
  const phone = normalizePhone(value);
  return phone.ok
    ? { ok: true, contact: { kind: 'phone', value: phone.e164 } }
    : { ok: false, reason: phone.reason };
}

export function displayHelpContact(contact: HelpContact): string {
  return contact.kind === 'phone' ? formatPhone(contact.value) : contact.value;
}

export function defaultHelpText(contact: HelpContact | null): string {
  return contact?.kind === 'phone' ? DEFAULT_HELP_PHONE : DEFAULT_HELP_EMAIL;
}

export interface ComplianceSettings {
  optOutFooter: string;
  footerOnFirstMessageEver: boolean;
  helpContact: HelpContact | null;
  /** Custom templates; null means the default. */
  helpText: string | null;
  optOutConfirmation: string | null;
  resubscribeConfirmation: string | null;
}

function text(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t === '' || t.length > max ? null : t;
}

/**
 * Reads whatever is stored. Lenient: a missing or malformed value becomes its default, so a bad
 * setting can never stop the worker from answering STOP.
 */
export function readComplianceSettings(settings: unknown): ComplianceSettings {
  const root =
    settings && typeof settings === 'object' ? (settings as Record<string, unknown>) : {};
  const c =
    root.compliance && typeof root.compliance === 'object'
      ? (root.compliance as Record<string, unknown>)
      : {};
  const footer = text(c.opt_out_footer, OPT_OUT_FOOTER_MAX);
  const contact = typeof c.help_contact === 'string' ? parseHelpContact(c.help_contact) : null;
  return {
    optOutFooter: footer && /stop/i.test(footer) ? footer : DEFAULT_OPT_OUT_FOOTER,
    footerOnFirstMessageEver:
      typeof c.footer_on_first_message_ever === 'boolean' ? c.footer_on_first_message_ever : true,
    helpContact: contact?.ok ? contact.contact : null,
    helpText: text(c.help_text, TEMPLATE_MAX),
    optOutConfirmation: text(c.opt_out_confirmation, TEMPLATE_MAX),
    resubscribeConfirmation: text(c.resubscribe_confirmation, TEMPLATE_MAX),
  };
}

/** Fills `{workspace}` and `{help_contact}`. The name is made GSM-safe (curly quotes → straight). */
export function renderComplianceText(
  template: string,
  vars: { workspaceName: string; helpContact: HelpContact | null },
): string {
  const name = toGsmSafe(vars.workspaceName.trim()).text;
  const contact = vars.helpContact ? displayHelpContact(vars.helpContact) : '';
  return template.replaceAll('{workspace}', name).replaceAll('{help_contact}', contact).trim();
}

export function isSingleGsmSegment(value: string): boolean {
  const info = countSegments(value);
  return info.segments === 1 && info.encoding === 'gsm7';
}

function firstFitting(candidates: string[]): string | null {
  return candidates.find((t) => t !== '' && isSingleGsmSegment(t)) ?? null;
}

/** The opt-out confirmation. Always returns a one-segment text: it must go out. */
export function optOutConfirmationText(c: ComplianceSettings, workspaceName: string): string {
  const vars = { workspaceName, helpContact: c.helpContact };
  return (
    firstFitting([
      ...(c.optOutConfirmation ? [renderComplianceText(c.optOutConfirmation, vars)] : []),
      renderComplianceText(DEFAULT_OPT_OUT_CONFIRMATION, vars),
    ]) ?? FALLBACK_OPT_OUT_CONFIRMATION
  );
}

export function resubscribeConfirmationText(c: ComplianceSettings, workspaceName: string): string {
  const vars = { workspaceName, helpContact: c.helpContact };
  return (
    firstFitting([
      ...(c.resubscribeConfirmation ? [renderComplianceText(c.resubscribeConfirmation, vars)] : []),
      renderComplianceText(DEFAULT_RESUBSCRIBE_CONFIRMATION, vars),
    ]) ?? FALLBACK_RESUBSCRIBE_CONFIRMATION
  );
}

export type HelpReply =
  { ok: true; text: string } | { ok: false; reason: 'no_help_contact' | 'too_long' };

/** The HELP reply. Needs a help contact (owner decision 2026-10-12) and must fit one GSM-7 segment. */
export function helpReplyText(c: ComplianceSettings, workspaceName: string): HelpReply {
  if (!c.helpContact) return { ok: false, reason: 'no_help_contact' };
  const vars = { workspaceName, helpContact: c.helpContact };
  const fallback = defaultHelpText(c.helpContact);
  const fitting = firstFitting([
    ...(c.helpText ? [renderComplianceText(c.helpText, vars)] : []),
    renderComplianceText(fallback, vars),
    renderComplianceText(fallback.replace('{workspace}: reply', 'Reply'), vars),
  ]);
  return fitting ? { ok: true, text: fitting } : { ok: false, reason: 'too_long' };
}

// ─────────────────────────── The settings screen ───────────────────────────

export interface ComplianceForm {
  optOutFooter: string;
  footerOnFirstMessageEver: boolean;
  helpContact: string;
  /** Empty means "use the default". */
  helpText: string;
  optOutConfirmation: string;
  resubscribeConfirmation: string;
}

export function toComplianceForm(c: ComplianceSettings): ComplianceForm {
  return {
    optOutFooter: c.optOutFooter,
    footerOnFirstMessageEver: c.footerOnFirstMessageEver,
    helpContact: c.helpContact ? displayHelpContact(c.helpContact) : '',
    helpText: c.helpText ?? '',
    optOutConfirmation: c.optOutConfirmation ?? '',
    resubscribeConfirmation: c.resubscribeConfirmation ?? '',
  };
}

export type ComplianceFormIssues = Partial<Record<keyof ComplianceForm, string>>;

/** Every rule the settings screen enforces, with the texts rendered for this workspace. */
export function checkComplianceForm(
  form: ComplianceForm,
  workspaceName: string,
): ComplianceFormIssues {
  const issues: ComplianceFormIssues = {};
  const footer = form.optOutFooter.trim();
  if (footer === '') issues.optOutFooter = 'Enter the opt-out wording';
  else if (footer.length > OPT_OUT_FOOTER_MAX) issues.optOutFooter = 'Use at most 60 characters';
  else if (!/stop/i.test(footer)) issues.optOutFooter = 'The footer must tell people to reply STOP';
  else if (countSegments(footer).encoding !== 'gsm7')
    issues.optOutFooter = 'Use plain characters only (no emoji or curly quotes)';

  let contact: HelpContact | null = null;
  if (form.helpContact.trim() !== '') {
    const parsed = parseHelpContact(form.helpContact);
    if (parsed.ok) contact = parsed.contact;
    else issues.helpContact = parsed.reason;
  }
  const vars = { workspaceName, helpContact: contact };

  const template = (
    key: 'helpText' | 'optOutConfirmation' | 'resubscribeConfirmation',
    fallback: string,
    must: { pattern: RegExp; message: string }[],
  ) => {
    const value = form[key].trim();
    if (value.length > TEMPLATE_MAX) {
      issues[key] = 'Use at most 200 characters';
      return;
    }
    const t = value === '' ? fallback : value;
    for (const rule of must) {
      if (!rule.pattern.test(t)) {
        issues[key] = rule.message;
        return;
      }
    }
    if (!isSingleGsmSegment(renderComplianceText(t, vars))) {
      issues[key] =
        'Once filled in, this must fit one plain-text segment (160 characters, no emoji or curly quotes)';
    }
  };
  template('optOutConfirmation', DEFAULT_OPT_OUT_CONFIRMATION, []);
  template('resubscribeConfirmation', DEFAULT_RESUBSCRIBE_CONFIRMATION, [
    { pattern: /stop/i, message: 'Tell people they can reply STOP' },
  ]);
  template('helpText', defaultHelpText(contact), [
    { pattern: /\{help_contact\}/, message: 'Include {help_contact} so people can reach you' },
    { pattern: /stop/i, message: 'Tell people they can reply STOP' },
  ]);
  return issues;
}

/** `settings.compliance` as stored (a type alias, so it fits a JSON column type). */
export type StoredCompliance = {
  opt_out_footer: string;
  footer_on_first_message_ever: boolean;
  help_contact: string | null;
  help_text: string | null;
  opt_out_confirmation: string | null;
  resubscribe_confirmation: string | null;
};

/** The stored form (snake_case, help contact normalized). Call after checkComplianceForm passed. */
export function toStoredCompliance(form: ComplianceForm): StoredCompliance {
  const contact = form.helpContact.trim() === '' ? null : parseHelpContact(form.helpContact);
  return {
    opt_out_footer: form.optOutFooter.trim(),
    footer_on_first_message_ever: form.footerOnFirstMessageEver,
    help_contact: contact?.ok ? contact.contact.value : null,
    help_text: form.helpText.trim() === '' ? null : form.helpText.trim(),
    opt_out_confirmation:
      form.optOutConfirmation.trim() === '' ? null : form.optOutConfirmation.trim(),
    resubscribe_confirmation:
      form.resubscribeConfirmation.trim() === '' ? null : form.resubscribeConfirmation.trim(),
  };
}
